import { useMutation, useQueryClient } from 'react-query';
import {
  patchApplicationAssignment,
  createNotification,
} from '@axo/shared/data-access/api/ace';
import { useAPI } from '@axo/shared/data-access/provider';
import { lock_api } from '@axo/shared/data-access/api';
import { aceAssignmentKeys } from '../../cache-keys/aceAssignmentKeys';
import { applicationAssignmentKeys } from '../../cache-keys/applicationAssignmentKeys';
import { bucketsKeys } from '../../cache-keys/bucketsKeys';
import { TransferApplicationParams } from './useTransferApplication.types';

export const useTransferApplication = () => {
  const queryClient = useQueryClient();
  const api = useAPI();

  return useMutation({
    mutationFn: async ({
      applicationId,
      newAssigneeId,
      assignmentType,
      marketCountry,
    }: TransferApplicationParams) => {
      const result = await patchApplicationAssignment(api)(applicationId, {
        AssignedAgentID: newAssigneeId,
        ...(assignmentType && { AssignmentType: assignmentType }),
        ...(marketCountry && { MarketCountry: marketCountry }),
      });

      try {
        await lock_api.unlock(api)(applicationId);
      } catch (error) {
        console.warn(
          'Lock release failed (application may not be locked):',
          error
        );
      }

      return result;
    },

    onSuccess: (_, variables) => {
      queryClient.invalidateQueries([
        aceAssignmentKeys.application(variables.applicationId),
      ]);

      queryClient.invalidateQueries(
        applicationAssignmentKeys.root({
          applicationID: variables.applicationId,
          marketCountry: variables.marketCountry,
        })
      );

      queryClient.invalidateQueries([
        bucketsKeys.applicationBucket(variables.applicationId),
      ]);

      queryClient.invalidateQueries(['locks', variables.applicationId]);
      queryClient.invalidateQueries(['user-locked-applications']);

      // Fire-and-forget notification to receiving agent
      createNotification(api)({
        Title: 'Application transferred to you',
        Body: variables.senderInitials
          ? `[transfer:${variables.senderInitials}] Application ${variables.applicationId} has been transferred to you.`
          : `Application ${variables.applicationId} has been transferred to you.`,
        ClientID: variables.newAssigneeId,
        MarketCountry: variables.marketCountry,
        LoanApplicationID: variables.applicationId,
      }).catch(() => {
        console.warn('Failed to send transfer notification');
      });

    },
  });
};

export function getErrorMessage(error: {
  response?: { status?: number };
  status?: number;
}): string {
  const status = error?.response?.status || error?.status;

  switch (status) {
    case 400:
      return 'The selected agent cannot receive applications. Please choose another.';
    case 403:
      return "You don't have permission to transfer this application.";
    case 409:
      return 'This application was just transferred by another user. Please refresh.';
    case 422:
      return 'Cannot transfer processed applications.';
    case 500:
      return 'Transfer failed. Please try again or contact support.';
    default:
      return 'Network error. Please check your connection and try again.';
  }
}
